/* eslint-disable react/jsx-key */
import React from "react";
import {
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  Spacer,
  GridItem,
  HStack,
  Button
} from "@chakra-ui/react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  Column,
  usePagination,
  useFilters
} from "react-table";

import {
  DropdownMenu,
  FilterDropdown,
  FilterTag,
  Pagination,
  Search
} from "components";

interface Props {
  columns: Column<any>[];
  data: any[];
  tableTitle?: string;
}

const DataTable: React.FC<Props> = ({ columns, data, tableTitle }: Props) => {
  const hiddenColumns = columns
    .map(col => col.id || col.accessor?.toString() || "")
    .filter(id => id != "id");

  const {
    setSortBy,
    getTableProps,
    getTableBodyProps,
    allColumns,
    prepareRow,
    setGlobalFilter,
    page,
    pageOptions,
    state: { globalFilter, pageIndex, filters },
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    setFilter,
    setAllFilters
  } = useTable<any>(
    {
      columns,
      data,
      initialState: {
        hiddenColumns
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const sortOptions: any[] = [
    {
      label: "None",
      callback: () => setSortBy([])
    }
  ];

  allColumns
    .filter(col => !col.disableSortBy)
    .forEach(col => {
      sortOptions.push({
        label: col.render("Header")?.valueOf(),
        callback: () => setSortBy([{ id: col.id }])
      });
      sortOptions.push({
        label: col.render("Header")?.valueOf() + " (desc)",
        callback: () => setSortBy([{ id: col.id, desc: true }])
      });
    });

  return (
    <>
      <SimpleGrid columns={4} w="100%" gap={5}>
        <GridItem colSpan={2}>
          <Search setFilter={setGlobalFilter} filter={globalFilter} />
        </GridItem>
        <Spacer />
        <FilterDropdown columns={allColumns} filters={filters} />
      </SimpleGrid>
      <HStack hidden={filters.length <= 0} spacing={5}>
        {filters.map(filter => (
          <FilterTag
            key={filter.id}
            label={filter.value}
            onClose={() => setFilter(filter.id, undefined)}
          />
        ))}
        <Button
          variant="link"
          size="sm"
          fontWeight="semibold"
          onClick={() => setAllFilters([])}
        >
          Clear All
        </Button>
      </HStack>
      <Table variant="unstyled" {...getTableProps()}>
        {(tableTitle || sortOptions.length > 1) && (
          <Thead>
            <Tr>
              <Th>
                <SimpleGrid columns={3} alignItems="center">
                  <Text>{tableTitle}</Text>
                  <Spacer />
                  {sortOptions.length > 1 && (
                    <HStack justifyContent="flex-end">
                      <Text>Sort By:</Text>

                      <DropdownMenu options={sortOptions} />
                    </HStack>
                  )}
                </SimpleGrid>
              </Th>
            </Tr>
          </Thead>
        )}
        <Tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <Td pl={0} pr={0} pb={0} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Pagination
        previousPage={previousPage}
        canPreviousPage={canPreviousPage}
        nextPage={nextPage}
        canNextPage={canNextPage}
        currentPage={Number(pageIndex) + 1}
        totalPages={pageOptions.length}
        gotoPage={gotoPage}
      />
    </>
  );
};

export default DataTable;
